import { useState, useEffect } from 'react'
import { Site, Area, AdminMapLink } from '../../types'
import { fetchWithAuth } from '../../utils/api'

function MapLinksManager() {
  const [sites, setSites] = useState<Site[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [mapLinks, setMapLinks] = useState<AdminMapLink[]>([])
  const [filteredLinks, setFilteredLinks] = useState<AdminMapLink[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingLink, setEditingLink] = useState<AdminMapLink | null>(null)
  const [formData, setFormData] = useState({
    area_id: '',
    url: '',
    date_time: '',
    pilot_name: '',
    comment: ''
  })

  useEffect(() => {
    fetchSites()
    fetchMapLinks()
  }, [])

  useEffect(() => {
    if (selectedSiteId) {
      fetchAreas(selectedSiteId)
    } else {
      setAreas([])
    }
  }, [selectedSiteId])

  useEffect(() => {
    if (!searchTerm) {
      setFilteredLinks(mapLinks)
    } else {
      const filtered = mapLinks.filter(link =>
        link.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.area_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.pilot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.url.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredLinks(filtered)
    }
  }, [mapLinks, searchTerm])

  const fetchSites = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/sites')
      if (!response.ok) {
        throw new Error('Failed to fetch sites')
      }
      const data = await response.json()
      setSites(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sites')
    } finally {
      setLoading(false)
    }
  }

  const fetchAreas = async (siteId: string) => {
    try {
      const response = await fetchWithAuth(`/api/admin/sites/${siteId}/areas`)
      if (!response.ok) {
        throw new Error('Failed to fetch areas')
      }
      const data = await response.json()
      setAreas(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load areas')
    }
  }

  const fetchMapLinks = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/map-links')
      if (!response.ok) {
        throw new Error('Failed to fetch map links')
      }
      const data = await response.json()
      setMapLinks(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map links')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    const payload = {
      area_id: parseInt(formData.area_id),
      url: formData.url,
      date_time: formData.date_time,
      pilot_name: formData.pilot_name,
      comment: formData.comment
    }

    try {
      const url = editingLink ? `/api/admin/map-links/${editingLink.id}` : '/api/admin/map-links'
      const method = editingLink ? 'PUT' : 'POST'
      
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error('Failed to save map link')
      }

      setSuccess(editingLink ? 'Map link updated successfully' : 'Map link created successfully')
      setShowForm(false)
      setEditingLink(null)
      setFormData({ area_id: '', url: '', date_time: '', pilot_name: '', comment: '' })
      fetchMapLinks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save map link')
    }
  }

  const handleEdit = (link: AdminMapLink) => {
    setEditingLink(link)
    
    // Find the site for this area
    const area = areas.find(a => a.id === link.area_id) || 
                 sites.flatMap(s => areas.filter(a => a.site_id === s.id)).find(a => a.id === link.area_id)
    
    if (area) {
      setSelectedSiteId(area.site_id.toString())
    }
    
    setFormData({
      area_id: link.area_id.toString(),
      url: link.url,
      date_time: new Date(link.date_time).toISOString().slice(0, 16), // Format for datetime-local input
      pilot_name: link.pilot_name,
      comment: link.comment
    })
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this map link?')) {
      return
    }

    try {
      const response = await fetchWithAuth(`/api/admin/map-links/${id}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete map link')
      }

      setSuccess('Map link deleted successfully')
      fetchMapLinks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete map link')
    }
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingLink(null)
    setFormData({ area_id: '', url: '', date_time: '', pilot_name: '', comment: '' })
    setSelectedSiteId('')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toISOString().slice(0, 19).replace('T', ' ')
  }

  if (loading) {
    return <div className="loading">Loading data...</div>
  }

  return (
    <div>
      <h2>Map Links Management</h2>
      
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem' }}>
          First, select a site to add a map link:
        </label>
        <select
          value={selectedSiteId}
          onChange={(e) => {
            setSelectedSiteId(e.target.value)
            setFormData({ ...formData, area_id: '' }) // Reset area selection
          }}
          style={{ padding: '0.5rem', marginRight: '1rem' }}
        >
          <option value="">-- Select a site --</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </select>

        {selectedSiteId && areas.length > 0 && (
          <button 
            className="btn btn-success" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add New Map Link'}
          </button>
        )}

        {selectedSiteId && areas.length === 0 && (
          <div style={{ marginTop: '0.5rem', color: '#666' }}>
            No areas found for this site. Please create an area first in the Areas tab.
          </div>
        )}
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingLink ? 'Edit Map Link' : 'Add New Map Link'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Site:</label>
              <div style={{ padding: '0.5rem', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
                {sites.find(s => s.id.toString() === selectedSiteId)?.name || 'No site selected'}
              </div>
            </div>
            
            <div className="form-group">
              <label>Area:</label>
              <select
                value={formData.area_id}
                onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                required
              >
                <option value="">-- Select an area --</option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Map URL:</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Date & Time:</label>
              <input
                type="datetime-local"
                value={formData.date_time}
                onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Pilot Name:</label>
              <input
                type="text"
                value={formData.pilot_name}
                onChange={(e) => setFormData({ ...formData, pilot_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Comment:</label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Optional comment about this mapping session"
              />
            </div>

            <button type="submit" className="btn btn-success">
              {editingLink ? 'Update Map Link' : 'Create Map Link'}
            </button>
            <button type="button" className="btn" onClick={resetForm}>
              Cancel
            </button>
          </form>
        </div>
      )}

      <div className="table-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by site, area, pilot, comment, or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredLinks.length === 0 ? (
          <div className="loading">
            {mapLinks.length === 0 ? 'No map links found' : 'No matches found'}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Area</th>
                <th>Map Link</th>
                <th>Date/Time</th>
                <th>Pilot</th>
                <th>Comment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLinks.map((link) => (
                <tr key={link.id}>
                  <td><span className="site-badge">{link.site_name}</span></td>
                  <td><span className="area-badge">{link.area_name}</span></td>
                  <td className="link-cell">
                    <button 
                      className="btn"
                      onClick={() => window.open(link.url, '_blank')}
                      title={link.url}
                      style={{ fontSize: '0.9rem', padding: '0.3rem 0.6rem' }}
                    >
                      🗺️ Open Map
                    </button>
                  </td>
                  <td className="date-cell">{formatDate(link.date_time)}</td>
                  <td><span className="pilot-badge">{link.pilot_name}</span></td>
                  <td style={{ fontStyle: 'italic', color: '#666' }}>{link.comment}</td>
                  <td>
                    <button 
                      className="btn" 
                      onClick={() => handleEdit(link)}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger" 
                      onClick={() => handleDelete(link.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
        Showing {filteredLinks.length} of {mapLinks.length} map links
      </div>
    </div>
  )
}

export default MapLinksManager